(() => {
  const norm = (value) => (value || "").trim().toLocaleLowerCase();
  const modelVariants = {
    "starlight grey": { "europa grey": "Black Grey", "mars red": "Red Grey" },
    "nightsky black": { "europa grey": "Black Black", "mars red": "Red Black" },
  };
  // The supplied "Scene" animation is a forward sequence: Stand → Lying Down → Stand.
  // The Lying Down pose settles at 7.6s; continuing forward to 14.4s performs the shorter return to Stand.
  const animationSegments = {
    stand: { start: 10.0, end: 14.4, initial: 0.04 },
    "lying down": { start: 0.04, end: 10.0 },
  };
  const defaultCameraOrbit = "-45deg 85deg auto";

  class Product3dViewer extends HTMLElement {
    async connectedCallback() {
      this.model = this.querySelector("model-viewer");
      this.modal = this.querySelector("x-modal");
      this.stage = this.querySelector(".product-3d-viewer__stage");
      this.loadingMessage = this.querySelector(".product-3d-viewer__loading");
      this.status = this.querySelector("[data-viewer-status]");
      this.customizeButton = this.querySelector(".product-3d-viewer__customize");
      this.controlsBackdrop = this.querySelector(".product-3d-viewer__controls-backdrop");
      this.variants = JSON.parse(
        this.querySelector("[data-product-variants]")?.textContent || "[]",
      );
      this.form = document.getElementById(this.formId);
      this.state = {
        station: "Starlight Grey",
        fabric: "Europa Grey",
        position: "Stand",
      };
      this.querySelector(".product-3d-launch").addEventListener("click", () => {
        document.documentElement.setAttribute(
          "data-product-3d-viewer-open",
          "",
        );
        this.closeControls();
        this.syncFromProduct();
        this.resetToStand();
      });
      this.modal.addEventListener("dialog:after-hide", () => {
        document.documentElement.removeAttribute("data-product-3d-viewer-open");
        this.closeControls();
      });
      this.customizeButton.addEventListener("click", () => this.toggleControls());
      this.controlsBackdrop.addEventListener("click", () => this.closeControls());
      this.querySelectorAll("[data-control] button").forEach((button) =>
        button.addEventListener("click", () => this.onControl(button)),
      );
      this.querySelector(".product-3d-viewer__reset").addEventListener(
        "click",
        () => this.resetToStand(),
      );
      this.model.addEventListener("load", () => {
        this.stage.setAttribute("data-loaded", "");
        this.stage.removeAttribute("data-error");
        this.render();
        this.seekTo(this.state.position, false);
      });
      this.model.addEventListener("error", (event) => {
        this.stage.setAttribute("data-error", "");
        this.loadingMessage.textContent =
          "The 3D model could not be loaded. Please refresh and try again.";
        console.error(
          "3D model failed to load:",
          event.detail?.type,
          event.detail?.sourceError?.message ||
            event.detail?.error?.message ||
            "unknown error",
        );
      });
      this.form?.addEventListener("variant:change", () =>
        this.syncFromProduct(),
      );
      this.syncFromProduct();
      await this.loadModelViewer();
    }
    async loadModelViewer() {
      if (!window.customElements.get("model-viewer")) {
        await import("https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js");
      }
      await window.customElements.whenDefined("model-viewer");
    }
    get formId() {
      return this.getAttribute("form-id");
    }
    toggleControls() {
      const isOpen = this.modal.hasAttribute("controls-open");
      this.modal.toggleAttribute("controls-open", !isOpen);
      this.customizeButton.setAttribute("aria-expanded", String(!isOpen));
    }
    closeControls() {
      this.modal?.removeAttribute("controls-open");
      this.customizeButton?.setAttribute("aria-expanded", "false");
    }
    optionValue(optionName) {
      const picker = document.querySelector(
        `variant-picker[form-id="${this.formId}"]`,
      );
      const fieldset = [...(picker?.querySelectorAll("fieldset") || [])].find(
        (item) =>
          norm(item.querySelector("legend")?.textContent).replace(/:$/, "") ===
          norm(optionName),
      );
      const input = fieldset?.querySelector(
        "input[data-option-position]:checked",
      );
      return input
        ? fieldset.querySelector(`label[for="${input.id}"]`)?.textContent.trim()
        : null;
    }
    syncFromProduct() {
      const station = this.optionValue(
        this.getAttribute("station-option-name"),
      );
      const fabric = this.optionValue(this.getAttribute("fabric-option-name"));
      if (modelVariants[norm(station)]) this.state.station = station;
      if (modelVariants[norm(this.state.station)]?.[norm(fabric)])
        this.state.fabric = fabric;
      this.render();
    }
    async onControl(button) {
      const group = button.closest("[data-control]");
      const control = group.dataset.control;
      this.state[control] = button.dataset.value;
      if (control === "station" || control === "fabric")
        await this.updateProductOption(control, button.dataset.value);
      if (control === "position") this.seekTo(button.dataset.value, true);
      this.render();
      this.status.textContent = `${group.previousElementSibling.textContent}: ${button.dataset.label || button.textContent.trim()}`;
    }
    updateProductOption(control, value) {
      const optionName = this.getAttribute(
        control === "station" ? "station-option-name" : "fabric-option-name",
      );
      const picker = document.querySelector(
        `variant-picker[form-id="${this.formId}"]`,
      );
      const fieldset = [...(picker?.querySelectorAll("fieldset") || [])].find(
        (item) =>
          norm(item.querySelector("legend")?.textContent).replace(/:$/, "") ===
          norm(optionName),
      );
      const label = [...(fieldset?.querySelectorAll("label") || [])].find(
        (item) => norm(item.textContent) === norm(value),
      );
      const input = label && document.getElementById(label.htmlFor);
      if (!input || input.checked) return;

      // Do not trigger VariantPicker's click handler here: it re-renders the product section and would close this modal.
      // Updating the selected option and form variant ID keeps the cart in sync while preserving the open 3D session.
      input.checked = true;
      const optionValues = [
        ...(picker?.querySelectorAll("fieldset") || []),
      ].map((item) => {
        const selected = item.querySelector(
          "input[data-option-position]:checked",
        );
        return selected
          ? item
              .querySelector(`label[for="${selected.id}"]`)
              ?.textContent.trim()
          : null;
      });
      const variant = this.variants.find((item) =>
        item.options?.every(
          (option, index) => norm(option) === norm(optionValues[index]),
        ),
      );
      const variantIdInput = this.form?.querySelector('[name="id"]');
      if (!variant || !variantIdInput) return;

      variantIdInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({ path: url.toString() }, "", url);
      this.form.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: { formId: this.formId, variant, previousVariant: null },
        }),
      );
    }
    render() {
      const variant =
        modelVariants[norm(this.state.station)]?.[norm(this.state.fabric)];
      if (variant && this.model.availableVariants?.includes(variant))
        this.model.variantName = variant;
      // x-modal moves itself to document.body while open, so its controls must be queried from the modal rather
      // than this component's original gallery location.
      this.modal.querySelectorAll("[data-control] button").forEach((button) => {
        const control = button.closest("[data-control]").dataset.control;
        button.setAttribute(
          "aria-pressed",
          String(norm(button.dataset.value) === norm(this.state[control])),
        );
      });
    }
    resetCamera() {
      // Restore only the camera framing. Configuration and the selected animation pose remain untouched.
      this.model.cameraOrbit = defaultCameraOrbit;
      this.model.cameraTarget = "auto auto auto";
      this.model.fieldOfView = "auto";
      this.model.jumpCameraToGoal?.();
    }
    resetToStand() {
      this.resetCamera();
      this.state.position = "Stand";
      this.seekTo(this.state.position, false);
      this.render();
    }
    seekTo(position, smooth) {
      const segment = animationSegments[norm(position)];
      if (!segment || !this.model.loaded) return;
      if (!smooth) {
        this.model.currentTime = segment.initial ?? segment.end;
        this.model.pause();
        return;
      }
      this.model.currentTime = segment.start;
      this.model.playbackRate = 1;
      this.model.play({ repetitions: 1 });
      clearInterval(this.seekTimer);
      this.seekTimer = setInterval(() => {
        if (this.model.currentTime >= segment.end) {
          this.model.currentTime = segment.end;
          this.model.pause();
          clearInterval(this.seekTimer);
        }
      }, 50);
    }
  }
  if (!customElements.get("product-3d-viewer"))
    customElements.define("product-3d-viewer", Product3dViewer);
})();
